/**
 * Service de autenticação
 */

import { api } from './api';
import { API_ENDPOINTS, LoginVendedorRequest, LoginVendedorResponse } from '@types';
import { SessionUtil, CarrinhoUtil } from '@utils';
import { carrinhoService } from './carrinho.service';

export class AuthService {
  /**
   * Realiza login do vendedor
   */
  async login(credentials: LoginVendedorRequest): Promise<LoginVendedorResponse> {
    try {
      console.log('[AUTH] Iniciando login...');

      const response = await api.post<LoginVendedorResponse>(
        API_ENDPOINTS.LOGIN,
        credentials
      );

      console.log('[AUTH] Resposta do login:', response);

      // Salva dados na sessão
      this.saveSession(response);

      console.log('[AUTH] Sessão salva, chamando loadPrevisaoAtiva...');

      // Recarrega previsão ativa se indPrevisao <> 0 AND indPedido == 0
      await this.loadPrevisaoAtiva(response);

      console.log('[AUTH] loadPrevisaoAtiva concluído');

      return response;
    } catch (error) {
      console.error('[AUTH] Erro ao fazer login:', error);
      throw error;
    }
  }

  /**
   * Salva dados do vendedor na sessão
   */
  private saveSession(data: LoginVendedorResponse): void {
    SessionUtil.setVendedor({
      vendId: data.id,
      vendCodigo: data.codigo,
      vendTipo: data.tipo,
      vendNome: data.nome,
      vendEmail: data.email,
      indPedido: data.pedido,
      indPrevisao: data.previsao,
      indPrevisaoId: data.previsaoId,
      indPrevisaoIni: data.previsao !== 0 ? data.previsao_inicio : undefined,
      indPrevisaoFim: data.previsao !== 0 ? data.previsao_fim : undefined,
      indPrevisaoConfirma: data.previsao !== 0 ? data.previsao_confirma : undefined,
    });
  }

  /**
   * Carrega previsão ativa do vendedor no carrinho
   * Apenas quando indPrevisao <> 0 AND indPedido == 0
   */
  private async loadPrevisaoAtiva(data: LoginVendedorResponse): Promise<void> {
    try {
      console.log('[AUTH] Verificando se deve carregar previsão ativa:', {
        previsao: data.previsao,
        pedido: data.pedido,
        codigo: data.codigo,
      });

      // Verifica se deve carregar previsão ativa
      // Converte pedido para número para comparação correta (API pode retornar string ou número)
      const pedidoNum = Number(data.pedido);

      if (data.previsao === 0 || pedidoNum !== 0) {
        console.log('[AUTH] Não carrega previsão ativa - Condição não atendida', {
          previsaoZero: data.previsao === 0,
          pedidoDiferenteZero: pedidoNum !== 0,
        });
        return;
      }

      console.log('[AUTH] Condição atendida - Buscando produtos da previsão ativa...');

      // Busca produtos da previsão ativa
      const produtosAPI = await carrinhoService.obterProdutosPrevisaoVendedor(
        data.previsao,
        data.codigo
      );

      console.log('[AUTH] Produtos retornados da API:', produtosAPI);

      // Se não há produtos na API, não faz nada
      if (!produtosAPI || produtosAPI.length === 0) {
        console.log('[AUTH] Nenhum produto retornado da API');
        return;
      }

      // Carrega carrinho atual
      const carrinhoAtual = carrinhoService.loadCarrinho('previsao');
      console.log('[AUTH] Carrinho atual:', {
        items: carrinhoAtual.items.size,
        total: carrinhoAtual.total,
      });

      // Mescla produtos da API com carrinho local
      // Prevalece o que está no carrinho local
      const carrinhoMesclado = CarrinhoUtil.mergePrevisaoProdutos(
        carrinhoAtual,
        produtosAPI
      );

      console.log('[AUTH] Carrinho mesclado:', {
        items: carrinhoMesclado.items.size,
        total: carrinhoMesclado.total,
      });

      // Salva carrinho mesclado
      carrinhoService.saveCarrinho(carrinhoMesclado);

      console.log(
        `[AUTH] Previsão ativa carregada: ${produtosAPI.length} produtos da API, ${carrinhoMesclado.items.size} produtos no carrinho`
      );
    } catch (error) {
      console.error('[AUTH] Erro ao carregar previsão ativa:', error);
      // Não propaga o erro para não bloquear o login
    }
  }

  /**
   * Realiza logout
   */
  logout(): void {
    SessionUtil.logout();
  }

  /**
   * Verifica se usuário está autenticado
   */
  isAuthenticated(): boolean {
    return SessionUtil.isAuthenticated();
  }

  /**
   * Obtém dados do vendedor autenticado
   */
  getVendedor() {
    return SessionUtil.getVendedor();
  }

  /**
   * Verifica se pode fazer previsão
   */
  canMakePrevisao(): boolean {
    return SessionUtil.canMakePrevisao();
  }

  /**
   * Verifica se pode fazer pedido
   */
  canMakePedido(): boolean {
    return SessionUtil.canMakePedido();
  }

  /**
   * Obtém tipo de operação atual
   */
  getOperationType(): 'previsao' | 'pedido' | null {
    return SessionUtil.getOperationType();
  }
}

export const authService = new AuthService();